import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellOff, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { EffectiveRecipientsCard } from '@/components/customers/EffectiveRecipientsCard';
import {
  useCreateCustomerContact,
  useCustomerContacts,
  useDeleteCustomerContact,
  useUpdateCustomerContact,
} from '@/hooks/useCustomerContacts';
import {
  useEffectiveRecipients,
  useNotificationRecipients,
  useUpdateNotificationRecipients,
} from '@/hooks/useMail';
import { formatDateTime } from '@/lib/format';
import type {
  CustomerContact,
  CustomerContactCreate,
  CustomerContactSource,
  CustomerContactUpdate,
  SuppressedRecipient,
} from '@/types/api';

/**
 * Kunden-Detail-Tab "Kontakte & Mails": Kontakte aus InvoiceNinja und
 * manuell angepinnte Empfänger, plus kundenweites Opt-out.
 *
 * Schalter (Wartungsmails/Stumm/Opt-out) feuern sofort ein PATCH/PUT —
 * ein nachgelagerter Speichern-Knopf wird bei Wartungsmails zu oft vergessen.
 *
 * Vorschläge aus Invoice Ninja entfallen: die IN-Kontakte stehen jetzt
 * selbst in der Tabelle. Ein zweiter Weg, dieselben Adressen als manuelle
 * Kopie anzulegen, wäre genau die Doppelpflege, die dieser Tab abschafft.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_PIN = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'IT-Techniker',
  receivesMails: true,
};

interface CustomerContactsTabProps {
  customerId: string;
  /**
   * Archivierter Kunde: lesen ja, ändern nein.
   *
   * Das Backend lässt die GETs durch und beantwortet jeden Schreibzugriff
   * mit 409 (siehe ``_kontakt_customer``/``_active_customer``). Die Maske
   * bietet die Schalter deshalb gar nicht erst an, statt sie ins Messer
   * laufen zu lassen.
   */
  readOnly?: boolean;
}

export function CustomerContactsTab({
  customerId,
  readOnly = false,
}: CustomerContactsTabProps): JSX.Element {
  const contactsQuery = useCustomerContacts(customerId);
  const recipientsQuery = useNotificationRecipients(customerId);
  const effectiveQuery = useEffectiveRecipients(customerId);
  const createContact = useCreateCustomerContact();
  const updateContact = useUpdateCustomerContact();
  const deleteContact = useDeleteCustomerContact();
  const updateRecipients = useUpdateNotificationRecipients();

  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState(EMPTY_PIN);

  const suppressedById = useMemo(
    () =>
      new Map<string, SuppressedRecipient>(
        (effectiveQuery.data?.suppressed ?? []).map((row) => [row.contact_id, row])
      ),
    [effectiveQuery.data]
  );

  const onPatch = useCallback(
    (contactId: string, body: CustomerContactUpdate) => {
      updateContact.mutate({ customerId, contactId, body });
    },
    [customerId, updateContact]
  );

  const onDelete = useCallback(
    (contactId: string) => {
      deleteContact.mutate({ customerId, contactId });
    },
    [customerId, deleteContact]
  );

  if (contactsQuery.isLoading) return <SkeletonRows rows={3} cols={2} />;
  if (contactsQuery.isError) return <ErrorAlert error={contactsQuery.error} />;
  if (!contactsQuery.data) return <ErrorAlert error="Keine Daten." />;

  const contacts = contactsQuery.data.data;
  const optOut = recipientsQuery.data?.opt_out ?? false;
  // Beide Quellen zählen: fällt der Effective-GET aus (500), darf das
  // Opt-out aus dem Empfänger-GET nicht verlorengehen - sonst behauptet jede
  // Zeile ein grünes "Ja".
  const effectiveOptOut = optOut || (effectiveQuery.data?.opt_out ?? false);
  // Ohne Antwort des Effective-GET ist der Stand unbekannt, nicht "wirksam":
  // ohne Aufstellung kennt die Zeile weder Dubletten noch Gründe.
  const effectiveKnown = effectiveQuery.data !== undefined;

  const resetPin = (): void => {
    setPin(EMPTY_PIN);
    createContact.reset();
  };

  const closePin = (): void => {
    setPinOpen(false);
    resetPin();
  };

  const pinEmail = pin.email.trim();
  const pinEmailInvalid = pinEmail !== '' && !EMAIL_RE.test(pinEmail);
  const pinHasIdentity =
    pin.firstName.trim() !== '' || pin.lastName.trim() !== '' || pinEmail !== '';
  const pinCanSubmit = pinHasIdentity && !pinEmailInvalid && !createContact.isPending;

  const submitPin = (): void => {
    if (!pinCanSubmit) return;
    const body: CustomerContactCreate = {
      first_name: pin.firstName.trim() || null,
      last_name: pin.lastName.trim() || null,
      email: pinEmail || null,
      phone: pin.phone.trim() || null,
      role: pin.role.trim() || null,
      receives_notifications: pin.receivesMails,
      is_primary: false,
    };
    createContact.mutate(
      { customerId, body },
      {
        onSuccess: () => {
          closePin();
        },
      }
    );
  };

  const patchingId =
    updateContact.isPending && updateContact.variables ? updateContact.variables.contactId : null;
  const deletingId =
    deleteContact.isPending && deleteContact.variables ? deleteContact.variables.contactId : null;

  const onOptOutChange = (next: boolean): void => {
    if (!recipientsQuery.data) return;
    updateRecipients.mutate({
      customerId,
      // PUT ist round-trip-sicher: unveränderte emails-Liste, nur opt_out kippen.
      body: { emails: recipientsQuery.data.emails, opt_out: next },
    });
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <EffectiveRecipientsCard customerId={customerId} />
        {/* Kein zweispaltiges Raster mehr: die Kontakte-Tabelle hat acht
            Spalten samt Freitextfeld "Rolle" und monospace-Adressen und stand
            in einer halbbreiten Karte neben einer Karte mit genau einer
            Checkbox - der Rest verschwand im Querscroll. */}
        <div className="flex flex-col gap-4">
          <Card
            title="Kontakte"
            description="Diese Kontakte erhalten Wartungs-Ankündigungen und Run-Mails des Kunden. Die Spalte „Wartungsmails“ sagt links, was tatsächlich rausgeht, und trägt rechts die beiden Schalter dazu. „Stumm“ überschreibt lokal, was InvoiceNinja liefert — für eigene Kontakte gibt es das nicht. Schalter greifen sofort."
            actions={
              readOnly ? undefined : (
                <Button
                  size="sm"
                  leftIcon={<Plus className="size-4" />}
                  onClick={() => {
                    resetPin();
                    setPinOpen(true);
                  }}
                  data-testid="pin-technician-open"
                >
                  Techniker anpinnen
                </Button>
              )
            }
          >
            {readOnly && (
              <p className="mb-3 text-sm text-muted" data-testid="contacts-readonly-hinweis">
                Der Kunde ist archiviert. Kontakte stehen hier zum Nachlesen, ändern lässt sich
                nichts mehr — dazu den Kunden zuerst wiederherstellen.
              </p>
            )}
            {updateContact.isError && <ErrorAlert className="mb-3" error={updateContact.error} />}
            {deleteContact.isError && <ErrorAlert className="mb-3" error={deleteContact.error} />}

            <div className={optOut ? 'opacity-50' : ''}>
              {contacts.length === 0 ? (
                <p className="text-sm text-muted">Keine Kontakte hinterlegt.</p>
              ) : (
                <ContactsTable
                  contacts={contacts}
                  optOut={effectiveOptOut}
                  effectiveKnown={effectiveKnown}
                  suppressedById={suppressedById}
                  readOnly={readOnly}
                  patchingId={patchingId}
                  deletingId={deletingId}
                  onPatch={onPatch}
                  onDelete={onDelete}
                />
              )}
            </div>
          </Card>

          <div className="flex flex-col gap-4">
            <Card
              title={
                <span className="inline-flex items-center gap-2">
                  <BellOff className="size-4" aria-hidden="true" />
                  Opt-out
                </span>
              }
              description="Der Kunde will keine automatischen Wartungsmails."
            >
              {recipientsQuery.isError && (
                <ErrorAlert className="mb-3" error={recipientsQuery.error} />
              )}
              {updateRecipients.isError && (
                <ErrorAlert className="mb-3" error={updateRecipients.error} />
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={optOut}
                  disabled={readOnly || !recipientsQuery.data || updateRecipients.isPending}
                  onChange={(e) => {
                    onOptOutChange(e.target.checked);
                  }}
                  className="size-4 accent-primary"
                />
                <span>Keine Benachrichtigungen an diesen Kunden senden</span>
              </label>
              {optOut && (
                <p className="mt-2 text-xs text-muted">
                  Mails werden für diesen Kunden weder automatisch versendet noch als Entwurf
                  angelegt.
                </p>
              )}
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        open={pinOpen}
        onClose={closePin}
        title="Techniker anpinnen"
        description="Ein manuell gepflegter Kontakt. Der InvoiceNinja-Sync fasst ihn nicht an."
        footer={
          <>
            <Button variant="ghost" onClick={closePin} disabled={createContact.isPending}>
              Abbrechen
            </Button>
            <Button
              onClick={submitPin}
              loading={createContact.isPending}
              disabled={!pinCanSubmit}
              data-testid="pin-technician-submit"
            >
              Anpinnen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {createContact.isError && <ErrorAlert error={createContact.error} />}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Vorname"
              value={pin.firstName}
              onChange={(e) => setPin((p) => ({ ...p, firstName: e.target.value }))}
              autoComplete="off"
            />
            <Input
              label="Nachname"
              value={pin.lastName}
              onChange={(e) => setPin((p) => ({ ...p, lastName: e.target.value }))}
              autoComplete="off"
            />
          </div>
          <Input
            label="E-Mail"
            type="email"
            value={pin.email}
            onChange={(e) => setPin((p) => ({ ...p, email: e.target.value }))}
            error={pinEmailInvalid ? 'Keine gültige E-Mail-Adresse.' : undefined}
            autoComplete="off"
          />
          <Input
            label="Telefon"
            type="tel"
            value={pin.phone}
            onChange={(e) => setPin((p) => ({ ...p, phone: e.target.value }))}
            autoComplete="off"
          />
          <Input
            label="Rolle"
            value={pin.role}
            onChange={(e) => setPin((p) => ({ ...p, role: e.target.value }))}
            placeholder="z.B. IT-Techniker"
            autoComplete="off"
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={pin.receivesMails}
              onChange={(e) => setPin((p) => ({ ...p, receivesMails: e.target.checked }))}
              className="size-4 accent-primary"
            />
            <span>bekommt Wartungsmails</span>
          </label>
        </div>
      </Dialog>
    </>
  );
}

function sourceBadge(source: CustomerContactSource, contactId: string): JSX.Element {
  if (source === 'invoice_ninja') {
    return (
      <Badge tone="primary" data-testid={`contact-source-${contactId}`}>
        InvoiceNinja
      </Badge>
    );
  }
  return (
    <Badge tone="neutral" data-testid={`contact-source-${contactId}`}>
      manuell
    </Badge>
  );
}

function originHint(contact: CustomerContact): string {
  if (contact.source === 'invoice_ninja') {
    if (contact.last_synced_at) {
      return `aus IN, Stand ${formatDateTime(contact.last_synced_at)}`;
    }
    return 'aus IN, noch kein Sync';
  }
  return `angelegt ${formatDateTime(contact.created_at)}`;
}

/**
 * Warum bekommt diese Zeile KEINE Mail? Drei Zustände, drei Wörter.
 *
 * Der Grund aus der Aufstellung schlägt das Flag an der Zeile: nur sie kennt
 * Dubletten und in InvoiceNinja verschwundene Kontakte.
 */
function nichtEmpfaengerZustand(
  contact: CustomerContact,
  suppressed: SuppressedRecipient | undefined
): { label: string; tone: 'warning' | 'neutral' } {
  const grund = suppressed?.reason;
  if (grund === 'muted' || (grund === undefined && contact.muted)) {
    return { label: 'stumm', tone: 'warning' };
  }
  if (grund === 'no_notifications' || grund === undefined) {
    return { label: 'nicht freigegeben', tone: 'neutral' };
  }
  // duplicate / no_email / gone_from_invoice_ninja: der Satz darunter sagt es
  // genauer, als ein drittes Wort es könnte.
  return { label: 'bekommt nicht', tone: 'neutral' };
}

/** Der Tooltip zur Spalte "Wartungsmails" - je Zeile der passende Satz. */
function zustandTooltip(
  contact: CustomerContact,
  lage: { optOut: boolean; effectiveKnown: boolean; bekommtMails: boolean }
): string {
  if (lage.optOut) {
    return 'Der Kunde hat alle automatischen Wartungsmails abbestellt (Opt-out). Das gilt für jeden Kontakt dieses Kunden.';
  }
  if (!lage.effectiveKnown) {
    return 'Der wirksame Empfängerstand konnte gerade nicht geladen werden.';
  }
  if (lage.bekommtMails) {
    return 'Diese Adresse steht im Verteiler und bekommt Ankündigung, Start-, Abschluss- und Berichtsmail.';
  }
  if (contact.muted) {
    return 'Lokal stumm geschaltet. Der Kontakt bleibt aus InvoiceNinja gespiegelt, bekommt aber keine Wartungsmails — das Häkchen aus InvoiceNinja lässt sich hier nicht ändern, nur überstimmen.';
  }
  if (!contact.receives_notifications) {
    return 'Für Wartungsmails nicht freigegeben. Neue InvoiceNinja-Kontakte kommen so herein; das Rechnungsmail-Flag von dort ist keine Freigabe.';
  }
  return 'Diese Zeile bekommt keine Mail — der Grund steht darunter.';
}

function wartungsmailsFiltertext(
  contact: CustomerContact,
  optOut: boolean,
  effectiveKnown: boolean,
  suppressedById: Map<string, SuppressedRecipient>
): string {
  if (optOut) return 'stumm';
  if (!effectiveKnown) return '';
  const suppressed = suppressedById.get(contact.id);
  const bekommtMails = suppressed === undefined && contact.effective;
  if (bekommtMails) return 'bekommt';
  return nichtEmpfaengerZustand(contact, suppressed).label;
}

function ContactsTable({
  contacts,
  optOut,
  effectiveKnown,
  suppressedById,
  readOnly,
  patchingId,
  deletingId,
  onPatch,
  onDelete,
}: {
  contacts: CustomerContact[];
  optOut: boolean;
  effectiveKnown: boolean;
  suppressedById: Map<string, SuppressedRecipient>;
  readOnly: boolean;
  patchingId: string | null;
  deletingId: string | null;
  onPatch: (contactId: string, body: CustomerContactUpdate) => void;
  onDelete: (contactId: string) => void;
}): JSX.Element {
  const columns = useMemo((): ReadonlyArray<Column<CustomerContact>> => {
    return [
      {
        id: 'name',
        header: 'Name',
        filterValue: (contact) => contact.display_name,
        cell: (contact) => (
          <>
            <div className="font-medium">{contact.display_name}</div>
            {contact.role ? <div className="text-xs text-muted">{contact.role}</div> : null}
          </>
        ),
      },
      {
        id: 'quelle',
        header: 'Quelle',
        filter: 'select',
        filterValue: (contact) => (contact.source === 'invoice_ninja' ? 'InvoiceNinja' : 'manuell'),
        cell: (contact) => (
          <>
            <div className="flex flex-wrap items-center gap-1">
              {sourceBadge(contact.source, contact.id)}
              {contact.is_primary ? (
                <Badge tone="success" data-testid={`contact-primary-${contact.id}`}>
                  Hauptkontakt
                </Badge>
              ) : null}
            </div>
            <div className="text-xs text-muted" data-testid={`contact-origin-${contact.id}`}>
              {originHint(contact)}
            </div>
          </>
        ),
      },
      {
        id: 'email',
        header: 'E-Mail',
        className: 'break-all font-mono',
        filterValue: (contact) => contact.email ?? '',
        cell: (contact) => contact.email ?? '–',
      },
      // Vorher drei Spalten zum selben Thema ohne ein Wort
      // Erklärung: ein Ergebnis-Badge und zwei Häkchen, die
      // es steuern. Wer im einen abwählte und im anderen
      // kurz noch "Ja" sah, klickte weiter. Jetzt eine
      // Spalte: Zustand links, Stellschrauben rechts.
      {
        id: 'wartungsmails',
        header: (
          <span title="Zustand und Stellschrauben in einem: „bekommt“ = die Adresse steht im Verteiler. „stumm“ = lokal übersteuert, gibt es nur für InvoiceNinja-Kontakte. „nicht freigegeben“ = niemand hat den Kontakt für Wartungsmails freigegeben.">
            Wartungsmails
          </span>
        ),
        filter: 'select',
        filterLabel: 'Wartungsmails',
        filterValue: (contact) =>
          wartungsmailsFiltertext(contact, optOut, effectiveKnown, suppressedById),
        cell: (contact) => {
          const isIn = contact.source === 'invoice_ninja';
          const suppressed = suppressedById.get(contact.id);
          // Die Aufstellung aus dem Effective-GET schlägt contact.effective: nur sie
          // kennt Dubletten (dieselbe Adresse zweimal - die zweite Zeile bekommt
          // nichts), während contact.effective jede Zeile für sich bewertet. Sonst
          // widerspricht die Zeile dem Kasten darüber.
          const bekommtMails = effectiveKnown && suppressed === undefined && contact.effective;
          const zustand = nichtEmpfaengerZustand(contact, suppressed);
          const disabled = readOnly || patchingId === contact.id;
          return (
            <div
              className="flex flex-col gap-1"
              data-testid={`contact-effective-${contact.id}`}
              title={zustandTooltip(contact, { optOut, effectiveKnown, bekommtMails })}
            >
              {optOut ? (
                <>
                  <Badge tone="warning">stumm</Badge>
                  <div className="text-xs text-muted">Opt-out des Kunden</div>
                </>
              ) : !effectiveKnown ? (
                <>
                  <Badge tone="neutral">–</Badge>
                  <div className="text-xs text-muted">Stand unbekannt</div>
                </>
              ) : bekommtMails ? (
                <Badge tone="success">bekommt</Badge>
              ) : (
                <>
                  <Badge tone={zustand.tone}>{zustand.label}</Badge>
                  {suppressed ? (
                    <div className="text-xs text-muted">{suppressed.reason_text}</div>
                  ) : null}
                </>
              )}
              {/* Die beiden Stellschrauben stehen jetzt unter ihrem Ergebnis
                  statt drei Spalten weiter rechts. */}
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={contact.receives_notifications}
                    disabled={disabled}
                    aria-label={`Wartungsmails für ${contact.display_name} freigeben`}
                    data-testid={`contact-mails-${contact.id}`}
                    onChange={(e) => onPatch(contact.id, { receives_notifications: e.target.checked })}
                  />
                  <span>freigegeben</span>
                </label>
                {isIn ? (
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      checked={contact.muted}
                      disabled={disabled}
                      aria-label={`Stumm schalten für ${contact.display_name}`}
                      data-testid={`contact-muted-${contact.id}`}
                      onChange={(e) => onPatch(contact.id, { muted: e.target.checked })}
                    />
                    <span>stumm</span>
                  </label>
                ) : null}
              </div>
              {/* Reiner Hinweis: send_email heißt in InvoiceNinja "bekommt
                  Rechnungsmails" und ist seit dem 10.09.2026 keine Freigabe für
                  Wartungsmails mehr. */}
              {isIn && contact.invoice_ninja_send_email != null && (
                <div className="text-xs text-muted" data-testid={`contact-in-hint-${contact.id}`}>
                  InvoiceNinja: Rechnungsmails{' '}
                  {contact.invoice_ninja_send_email ? 'ja' : 'nein'} (nur Hinweis)
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: 'telefon',
        header: 'Telefon',
        filterValue: (contact) => contact.phone ?? '',
        cell: (contact) => contact.phone ?? '–',
      },
      {
        id: 'rolle',
        header: 'Rolle',
        filter: 'select',
        filterValue: (contact) => contact.role ?? '',
        cell: (contact) => (
          <RoleCell
            displayName={contact.display_name}
            role={contact.role}
            disabled={readOnly || patchingId === contact.id}
            onCommit={(role) => onPatch(contact.id, { role })}
          />
        ),
      },
      {
        id: 'aktionen',
        header: <span className="sr-only">Aktionen</span>,
        filter: false,
        filterLabel: 'Aktionen',
        cell: (contact) =>
          contact.source === 'manual' && !readOnly ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-danger"
              leftIcon={<X className="size-4" />}
              aria-label={`${contact.display_name} löschen`}
              data-testid={`contact-delete-${contact.id}`}
              loading={deletingId === contact.id}
              disabled={deletingId === contact.id}
              onClick={() => onDelete(contact.id)}
            >
              Löschen
            </Button>
          ) : null,
      },
    ];
  }, [
    deletingId,
    effectiveKnown,
    onDelete,
    onPatch,
    optOut,
    patchingId,
    readOnly,
    suppressedById,
  ]);

  return (
    <DataTable
      caption="Kontakte des Kunden"
      columns={columns}
      rows={contacts}
      rowKey={(contact) => contact.id}
      storageKey="kunde-kontakte"
      rowTestId={(contact) => `contact-row-${contact.id}`}
      rowMuted={(contact) => suppressedById.get(contact.id) !== undefined || !contact.effective}
    />
  );
}

function RoleCell({
  displayName,
  role,
  disabled,
  onCommit,
}: {
  displayName: string;
  role: string | null | undefined;
  disabled: boolean;
  onCommit: (role: string) => void;
}): JSX.Element {
  const [value, setValue] = useState(role ?? '');

  useEffect(() => {
    setValue(role ?? '');
  }, [role]);

  const commit = (): void => {
    const next = value.trim();
    const current = (role ?? '').trim();
    if (next === current) {
      setValue(role ?? '');
      return;
    }
    setValue(next);
    onCommit(next);
  };

  return (
    <input
      type="text"
      value={value}
      disabled={disabled}
      placeholder="z.B. IT-Techniker"
      aria-label={`Rolle von ${displayName}`}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      className="w-full min-w-[8rem] rounded-md border border-border bg-surface px-2 py-1 text-sm text-text placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}
